import { useEffect, useMemo, useRef, useState } from 'react';
import {
  formatJalali,
  isoToJalali,
  jalaliMonthLength,
  jalaliToIso,
  JALALI_MONTHS,
  JALALI_WEEKDAYS,
  saturdayFirstDow,
  toFaDigits as fa,
} from '@kia/shared';

/**
 * Jalali (Shamsi) date picker (O1) — a drop-in for `<Input type="date">`. The operator sees and picks a
 * Persian date; the value stored/emitted is ALWAYS ISO Gregorian `YYYY-MM-DD` (storage stays unambiguous).
 * A lightweight month-grid popup (no dependency). `min`/`max` are ISO bounds (e.g. the M8 validity cap).
 */

const pad = (n: number) => String(n).padStart(2, '0');

function browserTodayIso(): string {
  const n = new Date();
  return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
}

export function JalaliDateInput({
  value,
  onValue,
  min,
  max,
  placeholder = 'انتخاب تاریخ',
  disabled,
}: {
  value: string;
  onValue: (iso: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const todayIso = useMemo(browserTodayIso, []);

  // The month being viewed (independent of the selected value): the value's month, else today's.
  const initJ = isoToJalali(value) ?? isoToJalali(todayIso)!;
  const [view, setView] = useState({ jy: initJ.jy, jm: initJ.jm });
  useEffect(() => {
    if (open) {
      const j = isoToJalali(value) ?? isoToJalali(todayIso)!;
      setView({ jy: j.jy, jm: j.jm });
    }
  }, [open, value, todayIso]);

  // Close on outside-click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shiftMonth = (delta: number) => {
    let { jy, jm } = view;
    jm += delta;
    if (jm < 1) {
      jm = 12;
      jy -= 1;
    } else if (jm > 12) {
      jm = 1;
      jy += 1;
    }
    setView({ jy, jm });
  };

  const firstIso = jalaliToIso(view.jy, view.jm, 1);
  const lead = saturdayFirstDow(firstIso); // blank cells before day 1
  const len = jalaliMonthLength(view.jy, view.jm);
  const cells: (number | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: len }, (_, i) => i + 1),
  ];

  const pick = (d: number) => {
    onValue(jalaliToIso(view.jy, view.jm, d));
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="input"
        readOnly
        disabled={disabled}
        value={value ? formatJalali(value) : ''}
        placeholder={placeholder}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{ cursor: disabled ? 'default' : 'pointer' }}
      />
      {open ? (
        <div
          role="dialog"
          aria-label="انتخاب تاریخ"
          style={{
            position: 'absolute',
            zIndex: 40,
            marginTop: 4,
            background: 'var(--surface, #fff)',
            border: '1px solid var(--line, #e5e7eb)',
            borderRadius: 10,
            boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
            padding: 10,
            width: 260,
          }}
        >
          <div
            className="row"
            style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}
          >
            <button
              type="button"
              className="linklike"
              onClick={() => shiftMonth(-1)}
              aria-label="ماه قبل"
            >
              ‹
            </button>
            <b style={{ fontSize: 13 }}>
              {JALALI_MONTHS[view.jm - 1]} {fa(view.jy)}
            </b>
            <button
              type="button"
              className="linklike"
              onClick={() => shiftMonth(1)}
              aria-label="ماه بعد"
            >
              ›
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
            {JALALI_WEEKDAYS.map((w) => (
              <div
                key={w}
                className="muted"
                style={{ textAlign: 'center', fontSize: 11, padding: 2 }}
              >
                {w}
              </div>
            ))}
            {cells.map((d, i) => {
              if (d === null) return <div key={`b${i}`} />;
              const iso = jalaliToIso(view.jy, view.jm, d);
              const isDisabled = (min && iso < min) || (max && iso > max);
              const isSel = iso === value;
              const isToday = iso === todayIso;
              return (
                <button
                  key={iso}
                  type="button"
                  disabled={!!isDisabled}
                  onClick={() => pick(d)}
                  style={{
                    padding: '5px 0',
                    fontSize: 12.5,
                    borderRadius: 6,
                    border: isToday ? '1px solid var(--brand, #1e3a8a)' : '1px solid transparent',
                    background: isSel ? 'var(--brand, #1e3a8a)' : 'transparent',
                    color: isSel ? '#fff' : isDisabled ? 'var(--muted, #9ca3af)' : 'inherit',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  {fa(d)}
                </button>
              );
            })}
          </div>
          <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
            <button
              type="button"
              className="linklike"
              onClick={() => {
                if (!((min && todayIso < min) || (max && todayIso > max))) {
                  onValue(todayIso);
                  setOpen(false);
                }
              }}
            >
              امروز
            </button>
            {value ? (
              <button
                type="button"
                className="linklike"
                onClick={() => {
                  onValue('');
                  setOpen(false);
                }}
              >
                پاک کردن
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
